import { useState } from "react"
import PropTypes from "prop-types"
import { useTranslation, Trans } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { Button, Collapse, Flex, Spin, Switch, Tooltip } from "antd"
import { UpOutlined, DownOutlined, InfoCircleOutlined } from "@ant-design/icons"
import { PAYMENT_HISTORY_STATUS } from "ui/Booking/Details/utils"
import { getRefundDetails, getAutoCollectWindowStatus } from "store/bookings"
import { updateReservationPaymentOption } from "store/reservations"
import { resendPaymentLink } from "store/upcomingExperiences"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"
import { useBreakpoints } from "modules/window/hooks/useBreakpoints"
import IconWithBg from "ui/IconWithBg"
import TakePaymentModal from "ui/Booking/Details/Body/PaymentSummary/Modals/TakePayment"
import AdjustBookingPriceModal from "ui/Booking/Details/Body/PaymentSummary/Modals/AdjustBookingPrice"
import RefundModal from "ui/Booking/Details/Body/PaymentHistory/Modals/Refund"
import CardHold from "ui/Booking/Details/Body/CardHold"
import BookingTag from "../../../BookingTag"
import PaymentTag from "../../../PaymentTag"
import ConfirmModal from "../ConfirmModal/ConfirmModal"
import styles from "../../index.module.css"

const GUEST_LABEL = {
  adult: { key: "booking.reservation_detail.breakdown_adults", fallback: "Adults" },
  child: { key: "booking.reservation_detail.breakdown_children", fallback: "Children" },
  infant: { key: "booking.reservation_detail.breakdown_infants", fallback: "Infants" },
  passenger: { key: "booking.reservation_detail.breakdown_passengers", fallback: "Passengers" },
  // Flat-rate pricing: a base "Flat Rate" line (no count) plus per-type extra-guest lines.
  flat_rate: { key: "booking.reservation_detail.breakdown_flat_rate", fallback: "Flat Rate" },
  extra_adult: { key: "booking.reservation_detail.breakdown_extra_adults", fallback: "Extra Adults" },
  extra_child: { key: "booking.reservation_detail.breakdown_extra_children", fallback: "Extra Children" },
  extra_infant: { key: "booking.reservation_detail.breakdown_extra_infants", fallback: "Extra Infants" },
  extra_passenger: { key: "booking.reservation_detail.breakdown_extra_passengers", fallback: "Extra Passengers" }
}

// e.g. "Deposit (Visa **** 1234)" — falls back gracefully when method/last4 are missing
const formatHistoryLabel = (entry) => {
  const method = entry.method ? entry.method.charAt(0).toUpperCase() + entry.method.slice(1) : ""
  const cardPart = entry.cardLast4 ? `**** ${entry.cardLast4}` : ""
  const detail = [method, cardPart].filter(Boolean).join(" ")
  return detail ? `${entry.type} (${detail})` : entry.type
}

const PaymentsPanel = ({ payments = {}, reservation = {}, history = [], historyLoading = false, onRefresh, collapsible = false }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { hasPermission } = usePermissions()
  const isUpdatingPaymentOption = useSelector((store) => store.reservations.paymentOption.loading)
  const breakpoint = useBreakpoints()
  const isMobile = ["xs", "xsm", "sm"].includes(breakpoint)
  // Accordion behaviour: always on mobile, and on demand in the modal (`collapsible`).
  const isCollapsible = isMobile || collapsible
  const [isAutoCollectEnabled, setIsAutoCollectEnabled] = useState(payments.isAutoCollectEnabledEnabled)
  const [isTakePaymentOpen, setIsTakePaymentOpen] = useState(false)
  const [isAdjustPriceOpen, setIsAdjustPriceOpen] = useState(false)
  const [isRefundOpen, setIsRefundOpen] = useState(false)
  const [isAutoCollectOffModalOpen, setIsAutoCollectOffModalOpen] = useState(false)
  const [isCollectConfirmOpen, setIsCollectConfirmOpen] = useState(false)
  const [isWithin24hModalOpen, setIsWithin24hModalOpen] = useState(false)
  const [pendingAutoCollectValue, setPendingAutoCollectValue] = useState(null)
  const [isCheckingWindow, setIsCheckingWindow] = useState(false)
  const [isExpanded, setIsExpanded] = useState(true)
  const [isResendingLink, setIsResendingLink] = useState(false)
  // Guide/staff without Bookings-edit permission can view but not act (mirrors old page).
  const canEditBooking = hasPermission(MODULES.BOOKINGS, ACTIONS.EDIT)
  const isPaidInFull = payments.isPaidInFull ?? payments.remainingBalance === 0
  const isMarketplaceBooking = reservation.bookingSource === "marketplace" || reservation.bookingSource === "website"
  // A still-pending request (not yet expired) can't take payment — it must be accepted first.
  // Only once a request expires does Collect Payment become available.
  const isPendingRequest = reservation.rawStatus?.toLowerCase() === "requested" && !reservation.isExpired
  // A canceled booking is terminal — no Collect Payment / Adjust Price / edit affordances.
  const isCanceled = reservation.rawStatus?.toLowerCase() === "canceled"
  const showCollectPayment = canEditBooking && !isPendingRequest && !isCanceled
  const canManagePayments = canEditBooking && !isCanceled

  // Auto-collect toggle visibility mirrors the old booking-details PaymentSummary: only for a
  // deposit option with an outstanding balance on a chargeable, non-unpaid, non-expired booking.
  // Without these gates the toggle wrongly shows even when there's nothing left to collect.
  const isExpiredRequested = Boolean(reservation.isExpired) && reservation.rawStatus?.toLowerCase() === "requested"
  const showAutoCollect =
    (payments.isDepositOption ?? reservation.paymentOption?.includes("deposit")) &&
    (payments.remainingBalance ?? 0) > 0 &&
    payments.isChargeable &&
    payments.paymentStatus !== "unpaid" &&
    !isExpiredRequested
  // Accordion collapses the body; otherwise it's always visible.
  const showBody = !isCollapsible || isExpanded

  const isLinkPayment = reservation.paymentOption?.includes("by_link")

  // Mirrors the old booking details: toggling maps to the deposit+balance payment option.
  // Returns the dispatch promise so the confirmation modal can keep its Redux-driven loading
  // state until the request settles, then refresh.
  const applyAutoCollect = (value) => {
    if (!reservation.uuid) return Promise.resolve()
    const balancePart = value ? "balance_later" : "balance_in_cash"
    const option = isLinkPayment ? `deposit_by_link_and_${balancePart}` : `deposit_and_${balancePart}`
    return Promise.resolve(dispatch(updateReservationPaymentOption(reservation.uuid, option))).then(() => onRefresh?.())
  }

  // When the trip is within the auto-collect window (24h out) the balance is charged
  // immediately, so warn first — same guard the old booking-details page applies. Only when
  // outside the window do we fall through to the normal on/off confirmation modals.
  const handleAutoCollectChange = async (checked) => {
    setIsCheckingWindow(true)
    const status = reservation.bookingId ? await Promise.resolve(dispatch(getAutoCollectWindowStatus(reservation.bookingId))) : null
    setIsCheckingWindow(false)
    if (status?.within_window) {
      setPendingAutoCollectValue(checked)
      setIsWithin24hModalOpen(true)
      return
    }
    if (checked) {
      setIsCollectConfirmOpen(true)
    } else {
      setIsAutoCollectOffModalOpen(true)
    }
  }

  const handleConfirmWithin24h = () => {
    applyAutoCollect(pendingAutoCollectValue).then(() => {
      setIsAutoCollectEnabled(pendingAutoCollectValue)
      setIsWithin24hModalOpen(false)
      setPendingAutoCollectValue(null)
    })
  }

  const handleConfirmCollectOn = () => {
    applyAutoCollect(true).then(() => {
      setIsAutoCollectEnabled(true)
      setIsCollectConfirmOpen(false)
    })
  }

  const handleConfirmAutoCollectOff = () => {
    applyAutoCollect(false).then(() => {
      setIsAutoCollectEnabled(false)
      setIsAutoCollectOffModalOpen(false)
    })
  }

  // Each reused modal refetches the old bookings store internally; refresh the
  // reservation detail on close so this panel reflects the change too.
  const closeAndRefresh = (setter) => () => {
    setter(false)
    onRefresh?.()
  }

  const openRefund = () => {
    setIsRefundOpen(true)
    if (reservation.uuid) dispatch(getRefundDetails(reservation.uuid))
  }

  // Once a link has already been sent, the balance box resends it (same booking-level
  // endpoint the upcoming-experience Action Queue uses) rather than opening Take Payment.
  const handleResendLink = () => {
    if (!reservation.uuid || isResendingLink) return
    setIsResendingLink(true)
    dispatch(resendPaymentLink(reservation.uuid))
      .catch(() => {})
      .finally(() => setIsResendingLink(false))
  }

  // `refund_attempt` rows aren't real transactions (the API returns one per attempt) — keep them
  // out of the rendered payment list.
  const transactions = history.filter((entry) => !entry.action)

  const renderHeaderAction = () => {
    const toggle = isCollapsible ? (
      <IconWithBg icon={isExpanded ? <UpOutlined /> : <DownOutlined />} onClick={() => setIsExpanded((prev) => !prev)} />
    ) : null

    // Adjust Booking Price is the only header action (Collect Payment lives in the balance box
    // below), shown as a button styled like Collect Payment rather than a pencil dropdown.
    const editAction = canManagePayments && (
      <Button type="primary" size="small" className={styles.collectBtn} onClick={() => setIsAdjustPriceOpen(true)}>
        {t("booking.reservation_detail.adjust_booking_price")}
      </Button>
    )

    if (!editAction && !toggle) return null
    return (
      <Flex align="center" gap={8}>
        {editAction}
        {toggle}
      </Flex>
    )
  }

  return (
    <div className={styles.paymentsPanel}>
      <Flex align="center" justify="space-between" className={styles.paymentsPanelHeader}>
        <span className={styles.panelTitle}>{t("booking.reservation_detail.payments_title")}</span>
        {renderHeaderAction()}
      </Flex>

      {showBody && (
      <>
      <div className={styles.paymentDepositInfo}>
        <span className={styles.depositLabel}>{payments.depositLabel}</span>
        <Flex align="center" gap={6} className={styles.depositMeta}>
          {payments.paymentTag && <PaymentTag label={payments.paymentTag.label} color={payments.paymentTag.color} />}
          {payments.collectionTag && <PaymentTag label={payments.collectionTag.label} color={payments.collectionTag.color} />}
        </Flex>
      </div>

      {isPaidInFull ? (
        <Flex vertical gap={8} className={styles.paidInFullBox}>
          <span className={styles.paidInFullLabel}>{t("booking.reservation_detail.paid_total")}</span>
          <Flex align="center" justify="space-between" gap={8}>
            <BookingTag color="success">{t("booking.reservation_detail.paid_in_full")}</BookingTag>
            {/* Collect stays available even at a zero balance (e.g. to take an extra/adjusted charge). */}
            {showCollectPayment && (
              <Button type="primary" size="small" className={styles.collectBtn} onClick={() => setIsTakePaymentOpen(true)}>
                {t("booking.reservation_detail.collect_payment")}
              </Button>
            )}
          </Flex>
        </Flex>
      ) : (
        <Flex align="center" justify="space-between" gap={12} className={`${styles.outstandingBox} ${payments.isCollectionFailed ? styles.outstandingBoxFailed : ""}`}>
          <Flex align="center" gap={8}>
            <span className={`${styles.outstandingIcon} ${payments.isCollectionFailed ? styles.outstandingIconFailed : ""}`}>$</span>
            <Flex vertical gap={0}>
              <span className={styles.outstandingLabel}>{t("booking.reservation_detail.outstanding_balance")}</span>
              <span className={styles.outstandingAmount}>${(payments.outstandingBalance ?? 0).toFixed(2)}</span>
            </Flex>
          </Flex>
          {payments.isLinkSent
            ? canManagePayments && (
              <Button type="primary" size="small" className={styles.collectBtn} onClick={handleResendLink} loading={isResendingLink}>
                {t("booking.reservation_detail.resend_link", "Resend Link")}
              </Button>
            )
            : showCollectPayment && (
              <Button type="primary" size="small" className={styles.collectBtn} onClick={() => setIsTakePaymentOpen(true)}>
                {t("booking.reservation_detail.collect_payment")}
              </Button>
            )}
        </Flex>
      )}

      <Flex vertical gap={4} className={styles.paymentBreakdown}>
        {(payments.guestLines || []).map((line) => (
          <Flex key={line.type} justify="space-between" className={styles.breakdownRow}>
            <span className={styles.breakdownLabel}>
              {t(GUEST_LABEL[line.type]?.key, { count: line.count, defaultValue: `{{count}} ${GUEST_LABEL[line.type]?.fallback ?? ""}` })}
            </span>
            <span className={styles.breakdownValue}>${(line.amount ?? 0).toFixed(2)}</span>
          </Flex>
        ))}
        {(payments.addOns || []).map((addOn) => (
          <Flex key={addOn.id} justify="space-between" className={styles.breakdownRow}>
            <span className={styles.breakdownLabel}>
              {t("booking.reservation_detail.breakdown_addon", { name: addOn.name, count: addOn.quantity, defaultValue: `${addOn.name} x {{count}}` })}
            </span>
            <span className={styles.breakdownValue}>${(addOn.amount ?? 0).toFixed(2)}</span>
          </Flex>
        ))}
        {payments.subtotal && (
          <Flex justify="space-between" align="center" className={styles.breakdownRow}>
            <Flex align="center" gap={4}>
              <span className={styles.breakdownLabel}>{t("booking.reservation_detail.subtotal")}</span>
              {payments.subtotal.reason && (
                <Tooltip title={payments.subtotal.reason}>
                  <InfoCircleOutlined className={styles.breakdownInfoIcon} />
                </Tooltip>
              )}
            </Flex>
            <span className={styles.breakdownValue}>
              {payments.subtotal.isAdjusted && payments.subtotal.originalAmount != null && (
                <span className={styles.breakdownOriginal}>${payments.subtotal.originalAmount.toFixed(2)}</span>
              )}
              ${(payments.subtotal.amount ?? 0).toFixed(2)}
              {payments.subtotal.isAdjusted && (
                <span className={styles.breakdownAdjustedNote}>({t("booking.reservation_detail.adjusted")})</span>
              )}
            </span>
          </Flex>
        )}
        {payments.taxesAndFeesBreakdown?.length > 0 ? (
          // Expandable Taxes & Fees, mirroring the new booking flow: total on the header row,
          // the per-fee breakdown (card processing, platform, etc.) revealed by the arrow.
          <Collapse
            ghost
            className={styles.taxesFeesCollapse}
            items={[
              {
                key: "taxes-fees",
                styles: { header: { padding: 0 }, body: { padding: "4px 0 0 16px" } },
                label: (
                  <Flex justify="space-between" className={styles.breakdownRow}>
                    <span className={styles.breakdownLabel}>{t("booking.reservation_detail.taxes_fees")}</span>
                    <span className={styles.breakdownValue}>${(payments.taxesAndFees ?? 0).toFixed(2)}</span>
                  </Flex>
                ),
                children: payments.taxesAndFeesBreakdown.map((fee) => (
                  <Flex key={fee.label} justify="space-between" className={styles.breakdownRow}>
                    <span className={styles.breakdownSubLabel}>{fee.label}</span>
                    <span className={styles.breakdownSubValue}>${(fee.amount ?? 0).toFixed(2)}</span>
                  </Flex>
                ))
              }
            ]}
          />
        ) : (
          <Flex justify="space-between" className={styles.breakdownRow}>
            <span className={styles.breakdownLabel}>{t("booking.reservation_detail.taxes_fees")}</span>
            <span className={styles.breakdownValue}>${(payments.taxesAndFees ?? 0).toFixed(2)}</span>
          </Flex>
        )}
        {payments.discount && (
          <Flex justify="space-between" className={styles.breakdownRow}>
            <span className={styles.breakdownLabel}>
              {t("booking.reservation_detail.discount", "Discount")}
              {payments.discount.code ? ` (${payments.discount.code})` : ""}
            </span>
            <span className={styles.breakdownValue}>-${(payments.discount.amount ?? 0).toFixed(2)}</span>
          </Flex>
        )}
        {(payments.priceAdjustments || []).map((adjustment) => (
          <Flex key={adjustment.id} justify="space-between" align="center" className={styles.breakdownRow}>
            <Flex align="center" gap={4}>
              <span className={styles.breakdownLabel}>
                {t("booking.reservation_detail.price_change", "Price Change")}
              </span>
              {adjustment.reason && (
                <Tooltip title={adjustment.reason}>
                  <InfoCircleOutlined className={styles.breakdownInfoIcon} />
                </Tooltip>
              )}
            </Flex>
            <span className={styles.breakdownValue}>
              {adjustment.amount > 0 ? "+" : "-"}${Math.abs(adjustment.amount).toFixed(2)}
            </span>
          </Flex>
        ))}
        <Flex justify="space-between" className={`${styles.breakdownRow} ${styles.breakdownTotal}`}>
          <span className={styles.breakdownLabel}>{t("booking.reservation_detail.total")}</span>
          <span className={styles.breakdownValue}>${(payments.total ?? 0).toFixed(2)}</span>
        </Flex>
        {payments.refundDue > 0 ? (
          <Flex justify="space-between" className={`${styles.breakdownRow} ${styles.breakdownRemaining}`}>
            <span className={styles.breakdownLabel}>{t("booking.reservation_detail.refund_due", "Refund Due")}</span>
            <span className={styles.breakdownValue}>${payments.refundDue.toFixed(2)}</span>
          </Flex>
        ) : (
          <Flex justify="space-between" className={`${styles.breakdownRow} ${styles.breakdownRemaining}`}>
            <span className={styles.breakdownLabel}>{t("booking.reservation_detail.remaining_balance")}</span>
            <span className={styles.breakdownValue}>${(payments.remainingBalance ?? 0).toFixed(2)}</span>
          </Flex>
        )}
      </Flex>

      {showAutoCollect && (
        <div className={styles.autoCollectRow}>
          <Flex align="center" justify="space-between">
            <Flex vertical gap={2}>
              <span className={styles.autoCollectLabel}>{t("booking.reservation_detail.auto_collect_balance")}</span>
              {isAutoCollectEnabled && payments.isAutoCollectEnabledDateDisplay && (
                <span className={styles.autoCollectDate2}>{payments.isAutoCollectEnabledDateDisplay}</span>
              )}
            </Flex>
            <Switch
              size="small"
              checked={isAutoCollectEnabled}
              onChange={handleAutoCollectChange}
              loading={isCheckingWindow || isUpdatingPaymentOption}
              disabled={isMarketplaceBooking || !canEditBooking}
            />
          </Flex>
        </div>
      )}

      {canManagePayments && (
        <CardHold
          holdHistory={reservation.holdHistory}
          bookingUUID={reservation.uuid}
          bookingId={reservation.bookingId}
          clientId={reservation.client?.user_id}
          onRefresh={onRefresh}
        />
      )}

      <div className={styles.paymentHistorySection}>
        <Flex align="center" justify="space-between" className={styles.paymentHistoryHeader}>
          <span className={styles.paymentHistoryTitle}>{t("booking.reservation_detail.payment_history")}</span>
          <button type="button" className={styles.refundLink} onClick={openRefund}>
            {t("booking.reservation_detail.refund")}
          </button>
        </Flex>
        {historyLoading && history.length === 0 && (
          <Flex justify="center" className="p-s"><Spin size="small" /></Flex>
        )}
        {!historyLoading && transactions.length === 0 && (
          <span className={styles.historyPaidOn}>{t("booking.reservation_detail.no_payments", "No payments yet.")}</span>
        )}
        {transactions.map((entry) => {
          const status = entry.status?.toLowerCase()
          const isFailed = status === PAYMENT_HISTORY_STATUS.FAILED
          const isPending = status === PAYMENT_HISTORY_STATUS.PENDING
          // A failed charge greys the amount out; a (successful) refund shows it red.
          let amountStatusClass = ""
          if (isFailed) amountStatusClass = styles.historyAmountFailed
          else if (entry.isRefund) amountStatusClass = styles.historyAmountRefund
          // Date column: a failed charge shows "Failed on" even though the API now also sends
          // paid_on, so gate on status first, then paid, then requested.
          let dateLabel = ""
          let dateValue = ""
          let isFailedDate = false
          if (isFailed && entry.failedOn) {
            dateLabel = t("booking.reservation_detail.failed_on")
            dateValue = entry.failedOn
            isFailedDate = true
          } else if (entry.paidOn) {
            dateLabel = entry.isRefund ? t("booking.reservation_detail.refunded_on", "Refunded on") : t("booking.reservation_detail.paid_on", "Paid on")
            dateValue = entry.paidOn
          } else if (entry.requestedOn) {
            dateLabel = t("booking.reservation_detail.requested_on")
            dateValue = entry.requestedOn
          }
          return (
            <Flex key={entry.id} justify="space-between" align="flex-start" gap={12} className={styles.historyRow}>
              <Flex vertical gap={2} className={styles.historyItemBody}>
                <Flex align="center" gap={6} wrap="wrap">
                  <span className={styles.historyCard}>{formatHistoryLabel(entry)}</span>
                  {isFailed && <BookingTag color="error">{t("booking.reservation_detail.payment_failed")}</BookingTag>}
                  {isPending && <BookingTag color="warning">{t("booking.reservation_detail.payment_pending")}</BookingTag>}
                </Flex>
                <span className={`${styles.historyAmount} ${amountStatusClass}`}>
                  {entry.isRefund ? "-" : ""}${Math.abs(entry.amount ?? 0).toFixed(2)}
                </span>
                {isFailed && entry.failureReason && (
                  <Tooltip title={entry.failureReason}>
                    <span className={styles.historyFailureReason}>
                      {t("booking.reservation_detail.failure_reason", { reason: entry.failureReason })}
                    </span>
                  </Tooltip>
                )}
              </Flex>
              <Flex vertical gap={2} align="flex-end">
                {dateValue && (
                  <>
                    <span className={isFailedDate ? styles.historyFailedLabel : styles.historyPaidOn}>{dateLabel}</span>
                    <span className={isFailedDate ? styles.historyFailedLabel : styles.historyDate}>{dateValue}</span>
                  </>
                )}
              </Flex>
            </Flex>
          )
        })}
      </div>
      </>
      )}

      {isTakePaymentOpen && (
        <TakePaymentModal
          onOpen={isTakePaymentOpen}
          onClose={closeAndRefresh(setIsTakePaymentOpen)}
          remainingBalance={payments.remainingBalance ?? payments.total}
          bookingUUID={reservation.uuid}
          bookingId={reservation.bookingId}
          bookingTitle={reservation.experience?.title}
          bookedOn={reservation.created}
          clientName={reservation.client?.name}
          clientEmail={reservation.client?.email}
          experienceAvatar={reservation.experienceAvatar}
          paymentOption={reservation.paymentOption}
          adjustmentOutstandingBase={payments.adjustmentOutstandingBase}
          bookingStatus={reservation.rawStatus}
        />
      )}

      {isAdjustPriceOpen && (
        <AdjustBookingPriceModal
          onOpen={isAdjustPriceOpen}
          onClose={closeAndRefresh(setIsAdjustPriceOpen)}
          bookingUUID={reservation.uuid}
          tripUUID={reservation.tripUUID}
          currentPrice={payments.total}
          paidAmount={payments.paidAmount}
          isMarketplaceBooking={isMarketplaceBooking}
          paymentOption={reservation.paymentOption}
        />
      )}

      {isRefundOpen && <RefundModal onOpen={isRefundOpen} onClose={closeAndRefresh(setIsRefundOpen)} bookingUUID={reservation.uuid} />}

      <ConfirmModal
        open={isAutoCollectOffModalOpen}
        title={t("booking.reservation_detail.auto_collect_off_title")}
        description={t("booking.reservation_detail.auto_collect_off_desc")}
        confirmLabel={t("global.confirm")}
        confirmLoading={isUpdatingPaymentOption}
        onConfirm={handleConfirmAutoCollectOff}
        onCancel={() => setIsAutoCollectOffModalOpen(false)}
      />

      <ConfirmModal
        open={isCollectConfirmOpen}
        title={t("booking.reservation_detail.collect_confirm_title")}
        description={
          payments.collectionScheduleDisplay ? (
            <Trans
              i18nKey="booking.reservation_detail.collect_confirm_desc"
              values={{ date: payments.collectionScheduleDisplay }}
              components={{ bold: <strong /> }}
            />
          ) : (
            t("booking.reservation_detail.collect_confirm_desc_no_date")
          )
        }
        confirmLabel={t("booking.reservation_detail.proceed")}
        confirmLoading={isUpdatingPaymentOption}
        onConfirm={handleConfirmCollectOn}
        onCancel={() => setIsCollectConfirmOpen(false)}
      />

      {/* Trip is within the 24h auto-collect window: balance is charged immediately either way. */}
      <ConfirmModal
        open={isWithin24hModalOpen}
        title={t("booking.auto_collect_window_title", "Trip is within 24 hours")}
        description={
          pendingAutoCollectValue
            ? t(
                "booking.auto_collect_window_message_on",
                "This booking is within 24 hours. The client will be charged the remaining balance immediately. Do you want to proceed?"
              )
            : t(
                "booking.auto_collect_window_message_off",
                "This booking is within 24 hours. Turning off auto-collect will cancel the automatic charge of the remaining balance. Do you want to proceed?"
              )
        }
        confirmLabel={t("booking.reservation_detail.proceed")}
        confirmLoading={isUpdatingPaymentOption}
        onConfirm={handleConfirmWithin24h}
        onCancel={() => {
          setIsWithin24hModalOpen(false)
          setPendingAutoCollectValue(null)
        }}
      />
    </div>
  )
}

PaymentsPanel.propTypes = {
  payments: PropTypes.object,
  reservation: PropTypes.object,
  history: PropTypes.array,
  historyLoading: PropTypes.bool,
  onRefresh: PropTypes.func,
  collapsible: PropTypes.bool
}

export default PaymentsPanel
